import { useEffect, useMemo, useState } from 'react';
import { X, Sparkles, ImageIcon } from 'lucide-react';
import type { Campaign, SceneReview } from '../../../shared/types';
import { pb, errorText } from '../lib/pb';
import { driveThumbnailUrl } from '../lib/drive';

/**
 * The image library: every image this influencer has ever had APPROVED, ready
 * to reuse instead of paying for a new generation.
 *
 * Hard rules, not preferences:
 *  - Same influencer only — these images have the influencer in them.
 *  - Rejected/failed generations never appear: a rejected image's URL is
 *    overwritten by its re-render, so `decision = approve` is the whole filter.
 *  - Drive-backed only (provider links expire), which also excludes the dead
 *    v1 imports automatically.
 *  - An image holding a DIFFERENT product is hidden entirely, not down-ranked —
 *    the wrong bottle in hand is unusable, full stop.
 *
 * Suggestions on top: same room and same product-in-hand state as the scene
 * being filled. The human always chooses; nothing is auto-matched.
 */

export interface LibraryPick {
  url: string;
  label: string;
}

interface Entry {
  id: string;
  url: string;
  locationTag: string;
  holding: string;
  productId: string;
  label: string;
}

export default function ImageLibrary({
  influencerId,
  productId,
  locationTag,
  holdingProduct,
  excludeId,
  onPick,
  onClose,
}: {
  influencerId: string;
  productId: string;
  /** The scene being filled — drives the "Suggested" section. */
  locationTag?: string;
  holdingProduct?: string;
  /** A review row to leave out (replacing an image with itself is a no-op). */
  excludeId?: string;
  onPick: (pick: LibraryPick) => void;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = (await pb.collection('scene_reviews').getFullList({
          filter:
            `kind = "image" && decision = "approve" && failed = false && ` +
            `media_expired = false && drive_url != "" && ` +
            `campaign.influencer = "${influencerId}"`,
          expand: 'campaign,campaign.product',
          sort: '-created',
          batch: 200,
        })) as unknown as SceneReview[];

        if (cancelled) return;
        setEntries(
          rows
            .filter((r) => r.id !== excludeId)
            .map((r) => {
              const campaign = r.expand?.campaign as Campaign | undefined;
              const productName =
                (campaign?.expand?.product as { name?: string } | undefined)?.name ?? '';
              return {
                id: r.id,
                url: r.drive_url,
                locationTag: r.location_tag ?? '',
                holding: r.holding_product ?? '',
                productId: campaign?.product ?? '',
                label: `#${campaign?.seq ?? '?'} · scene ${r.scene_index + 1}${
                  r.holding_product === 'YES' && productName ? ` · ${productName}` : ''
                }`,
              };
            })
            // The hard product rule: a different product in hand is hidden
            // entirely. Product-free images are always fine.
            .filter((e) => e.holding !== 'YES' || e.productId === productId)
            // A revision copies its parent's image rows verbatim, so the same
            // Drive file would otherwise be offered once per revision.
            .filter((e, i, all) => all.findIndex((o) => o.url === e.url) === i)
        );
      } catch (err) {
        if (!cancelled) setError(errorText(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [influencerId, productId, excludeId]);

  const { suggested, rest } = useMemo(() => {
    if (!entries) return { suggested: [], rest: [] };
    const wantHolding = holdingProduct === 'YES' ? 'YES' : 'NO';
    const isSuggested = (e: Entry) =>
      Boolean(locationTag) &&
      e.locationTag === locationTag &&
      (e.holding || 'NO') === wantHolding;
    return {
      suggested: entries.filter(isSuggested),
      rest: entries.filter((e) => !isSuggested(e)),
    };
  }, [entries, locationTag, holdingProduct]);

  const grid = (items: Entry[]) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {items.map((e) => (
        <button
          key={e.id}
          onClick={() => onPick({ url: e.url, label: e.label })}
          className="group text-left rounded-xl overflow-hidden border border-natural-border hover:border-natural-sage transition-colors cursor-pointer bg-stone-100"
          title={e.label}
        >
          <img
            src={driveThumbnailUrl(e.url, 'w300')}
            alt={e.label}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full aspect-[3/4] object-cover group-hover:opacity-90"
          />
          <p className="px-2 py-1.5 text-[10px] font-mono text-natural-text/55 truncate">
            {e.label}
          </p>
        </button>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl border border-natural-border shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-serif text-base font-medium">Reuse an existing image</h3>
            <p className="text-[11px] text-natural-text/50 mt-0.5">
              Previously approved images of this influencer. Picking one costs nothing.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-natural-text/40 hover:bg-stone-100 cursor-pointer"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        {entries === null && !error && (
          <p className="text-xs text-natural-text/45 py-8 text-center">Loading the library…</p>
        )}

        {entries !== null && entries.length === 0 && (
          <div className="text-center py-8 space-y-2">
            <ImageIcon size={20} className="mx-auto text-natural-text/25" />
            <p className="text-xs text-natural-text/50">
              No reusable images for this influencer yet — approved images from finished renders
              will appear here.
            </p>
          </div>
        )}

        {suggested.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-natural-sage flex items-center gap-1.5">
              <Sparkles size={11} />
              Suggested — same room, same product state
            </p>
            {grid(suggested)}
          </div>
        )}

        {rest.length > 0 && (
          <div className="space-y-2">
            {suggested.length > 0 && (
              <p className="text-[10px] uppercase tracking-wider font-bold text-natural-text/40">
                Everything else
              </p>
            )}
            {grid(rest)}
          </div>
        )}
      </div>
    </div>
  );
}
